import React from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Mail, Send, Users, Eye, Plus, Trash2, X, Save, Archive, ExternalLink, ClipboardCopy, Radio, Check, Image as ImageIcon, Upload, Crop } from "lucide-react";
import { ImageCropModal } from "../components/ImageCropModal";
import { draftKey, saveDraft, loadDraft, clearDraft, formatDraftAge } from "../lib/draft";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB",
    { day:"numeric", month:"short", year:"numeric" }); }
  catch { return iso; }
}

export default function Newsletter() {
  const [tab, setTab] = React.useState("issues");
  const [issues, setIssues] = React.useState(null);
  const [subsInfo, setSubsInfo] = React.useState(null);
  const [candidates, setCandidates] = React.useState([]);
  const [editing, setEditing] = React.useState(null);
  const [previewHtml, setPreviewHtml] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [announcing, setAnnouncing] = React.useState(false);
  const [imgBusy, setImgBusy] = React.useState(false);
  const fileInputRef = React.useRef(null);
  const [cropOpen, setCropOpen] = React.useState(false);

  // Called after ImageCropModal uploads the cropped PNG. Adds it to
  // header_candidates, **auto-selects it as the active header** (Elle's
  // mental model: "I just attached this, so it should be what shows"),
  // and auto-saves the draft so the image is immediately visible in
  // Preview and persists across refresh.
  const onCroppedHeader = async (img) => {
    if (!editing) return;
    const imgId = img.url.split("/").pop().replace(/\.png$/, "");
    const nextEditing = {
      ...editing,
      header_candidates: [...(editing.header_candidates || []),
        { id: imgId, url: img.url, width: img.w, height: img.h,
          uploaded_at: new Date().toISOString(),
          filename: `cropped-${img.preset}.png`,
          alt: img.alt || "" }],
      header_image_id: imgId,  // Always select the newly-uploaded image.
    };
    setEditing(nextEditing);
    try {
      await saveIssue(nextEditing);
      toast.success("Header attached & saved.");
    } catch {
      toast.message("Header attached — click Save draft to persist.");
    }
  };

  const openCropperForHeader = async () => {
    if (!editing) return;
    // Auto-save draft first so header attaches to a real issue row.
    if (editing.isNew) {
      toast.message("Saving draft first…");
      await saveIssue();
      await new Promise(r => setTimeout(r, 50));
    }
    setCropOpen(true);
  };

  // Upload a user-supplied image (PNG/JPG/WebP) as the newsletter header.
  // She generates externally (Claude/Midjourney/Google AI Studio web UI/etc)
  // and uploads here — no Gemini API cost, no daily quota, no credit drain.
  const uploadHeaderImage = async (fileList) => {
    if (!editing) return;
    const file = fileList && fileList[0];
    if (!file) return;
    // Auto-save the draft first if brand new so we have a slug to attach to.
    if (editing.isNew) {
      toast.message("Saving draft first…");
      await saveIssue();
      await new Promise(r => setTimeout(r, 50));
    }
    setImgBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/images/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data.ok) { toast.error("Upload failed."); return; }
      setEditing(prev => prev ? {
        ...prev,
        header_candidates: [ ...(prev.header_candidates || []),
                             { id: data.id, url: data.url, width: data.width,
                               height: data.height, uploaded_at: new Date().toISOString(),
                               filename: file.name }],
        // Auto-select the freshly-uploaded image if nothing's selected yet.
        header_image_id: prev.header_image_id || data.id,
      } : prev);
      toast.success(`Uploaded ${file.name} · ${data.width}×${data.height}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setImgBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const chooseHeader = async (imageId) => {
    if (!editing) return;
    const next = { ...editing, header_image_id: imageId };
    setEditing(next);
    try {
      await saveIssue(next);
      toast.success("Header selected.");
    } catch {
      toast.message("Selection changed — click Save draft to persist.");
    }
  };

  const clearHeader = async () => {
    if (!editing) return;
    const next = { ...editing, header_image_id: null };
    setEditing(next);
    try { await saveIssue(next); } catch {}
  };

  const removeCandidate = async (imageId) => {
    if (!editing) return;
    const next = {
      ...editing,
      header_candidates: (editing.header_candidates || []).filter(c => c.id !== imageId),
      header_image_id: editing.header_image_id === imageId ? null : editing.header_image_id,
    };
    setEditing(next);
    try {
      await saveIssue(next);
      toast.success("Image removed.");
    } catch {
      toast.error("Remove failed — please try again.");
    }
  };
  const [draftMeta, setDraftMeta] = React.useState(null);

  const currentKey = React.useMemo(() => {
    if (!editing) return null;
    return draftKey("newsletter", editing.isNew ? "new" : editing.slug);
  }, [editing]);

  React.useEffect(() => {
    if (!editing || !currentKey) return;
    const t = setTimeout(() => {
      const snapshot = {
        isNew: !!editing.isNew,
        slug: editing.slug || null,
        subject: editing.subject || "",
        intro_markdown: editing.intro_markdown || "",
        outro_markdown: editing.outro_markdown || "",
        brief_ids: editing.brief_ids || [],
        custom_sections: editing.custom_sections || [],
      };
      saveDraft(currentKey, snapshot);
      setDraftMeta({ saved_at: Date.now() });
    }, 800);
    return () => clearTimeout(t);
  }, [editing, currentKey]);

  const load = async () => {
    const [i, s, c] = await Promise.all([
      api.get("/newsletter/issues"),
      api.get("/newsletter/subscribers"),
      api.get("/newsletter/briefs"),
    ]);
    setIssues(i.data);
    setSubsInfo(s.data);
    setCandidates(c.data);
  };
  React.useEffect(() => { load().catch(() => toast.error("Couldn't load newsletter data.")); }, []);

  const newIssue = () => {
    const existing = loadDraft(draftKey("newsletter", "new"));
    if (existing && existing.data) {
      setEditing({ ...existing.data, isNew: true });
      setDraftMeta({ saved_at: existing.saved_at });
      toast.info("Restored your unsaved draft.");
      return;
    }
    setEditing({
      isNew: true, subject: "", intro_markdown: "", outro_markdown: "",
      brief_ids: [], custom_sections: [],
    });
    setDraftMeta(null);
  };

  const openIssue = (i) => {
    const existing = loadDraft(draftKey("newsletter", i.slug));
    if (existing && existing.data) {
      const keep = window.confirm(
        `Unsaved local edits for "${i.subject}" (${formatDraftAge(existing.saved_at)}).\n\n` +
        `OK = restore unsaved edits\nCancel = load the server copy`
      );
      if (keep) {
        setEditing({ ...i, ...existing.data, slug: i.slug, isNew: false, status: i.status });
        setDraftMeta({ saved_at: existing.saved_at });
        return;
      }
      clearDraft(draftKey("newsletter", i.slug));
    }
    setEditing({ ...i });
    setDraftMeta(null);
  };

  const discardDraft = () => {
    if (!currentKey) return;
    clearDraft(currentKey);
    setDraftMeta(null);
    toast.info("Local draft discarded.");
  };

  const saveIssue = async (override = null) => {
    const src = override || editing;
    if (!src.subject.trim()) return toast.error("Subject required.");
    setBusy(true);
    try {
      if (src.isNew) {
        const { data } = await api.post("/newsletter/issues", {
          subject: src.subject,
          intro_markdown: src.intro_markdown,
          outro_markdown: src.outro_markdown,
          brief_ids: src.brief_ids,
          custom_sections: src.custom_sections,
        });
        if (!override) toast.success("Draft saved.");
        if (currentKey) clearDraft(currentKey);
        setEditing({...data, isNew: false});
        setDraftMeta(null);
      } else {
        const { data } = await api.put(`/newsletter/issues/${src.slug}`, {
          subject: src.subject,
          intro_markdown: src.intro_markdown,
          outro_markdown: src.outro_markdown,
          brief_ids: src.brief_ids,
          custom_sections: src.custom_sections,
          header_image_id: src.header_image_id || null,
          header_candidates: src.header_candidates || [],
          image_prompt: src.image_prompt || "",
        });
        if (!override) toast.success("Saved.");
        if (currentKey) clearDraft(currentKey);
        setEditing({...data, isNew: false});
        setDraftMeta(null);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed. Your work is safe — restored on next open.");
    } finally { setBusy(false); }
  };

  const preview = async () => {
    // Auto-save whatever is currently in the editor before rendering the
    // preview — otherwise the preview pulls the last *saved* state from the
    // server, which silently omits any unsaved edits and looks like a bug.
    if (editing.isNew) { await saveIssue(); return; }
    setBusy(true);
    try {
      await api.put(`/newsletter/issues/${editing.slug}`, {
        subject: editing.subject,
        intro_markdown: editing.intro_markdown,
        outro_markdown: editing.outro_markdown,
        brief_ids: editing.brief_ids,
        custom_sections: editing.custom_sections,
      });
      const { data } = await api.get(`/newsletter/issues/${editing.slug}/preview`);
      setPreviewHtml(data.html);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed.");
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!window.confirm(`Send "${editing.subject}" to ${subsInfo?.stats.confirmed || 0} confirmed subscriber(s)? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/newsletter/issues/${editing.slug}/send`);
      toast.success(`Sent to ${data.sent} recipient(s). ${data.failed ? `${data.failed} failed.` : ""}`);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
    } finally { setBusy(false); }
  };

  const toggleBrief = (id) => {
    const ids = editing.brief_ids.includes(id)
      ? editing.brief_ids.filter(x => x !== id)
      : [...editing.brief_ids, id];
    setEditing({...editing, brief_ids: ids});
  };

  const addSection = (position = "after") => setEditing({
    ...editing,
    custom_sections: [
      ...(editing.custom_sections || []),
      // Stable per-row id — used as React key so removing row N doesn't
      // make the rows below it re-use row N's DOM + state.
      // `position` controls whether this section renders before or after
      // the daily-brief block. Defaults to "after" for backwards compat.
      { _id: `sec-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        title: "", body_markdown: "", position },
    ],
  });
  const updateSection = (i, field, value) => {
    const next = [...editing.custom_sections];
    next[i] = {...next[i], [field]: value};
    setEditing({...editing, custom_sections: next});
  };
  const removeSection = (i) => setEditing({
    ...editing, custom_sections: editing.custom_sections.filter((_, idx) => idx !== i)
  });

  const deleteIssue = async (slug) => {
    if (!window.confirm("Delete this draft?")) return;
    try {
      await api.delete(`/newsletter/issues/${slug}`);
      toast.success("Deleted.");
      await load();
    } catch (e) { toast.error("Delete failed."); }
  };

  // ============ Substack copy-and-paste helper ============
  //
  // We can't post to Substack on her behalf (and wouldn't want to — storing
  // her Substack session is a risk she explicitly opted out of). Instead,
  // one click → markdown on clipboard + Substack editor opens in a new tab.
  const copyForSubstack = async () => {
    if (!editing || editing.isNew) return toast.error("Save the draft first.");
    setBusy(true);
    try {
      // Auto-save first — same rationale as preview: otherwise the Substack
      // markdown would reflect the last saved state, not the editor.
      await api.put(`/newsletter/issues/${editing.slug}`, {
        subject: editing.subject,
        intro_markdown: editing.intro_markdown,
        outro_markdown: editing.outro_markdown,
        brief_ids: editing.brief_ids,
        custom_sections: editing.custom_sections,
      });
      const { data } = await api.get(`/newsletter/issues/${editing.slug}/substack`);
      const clipText = `# ${data.subject}\n\n*${data.subtitle}*\n\n${data.markdown}`;
      try {
        await navigator.clipboard.writeText(clipText);
      } catch {
        // Clipboard may fail on non-HTTPS or on some browsers. Fall back to
        // a hidden textarea — not pretty, but reliable.
        const ta = document.createElement("textarea");
        ta.value = clipText;
        ta.style.position = "fixed"; ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Copied. Substack opening in a new tab…");
      window.open(data.publish_url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't prepare Substack version.");
    } finally {
      setBusy(false);
    }
  };

  // Draft a Bluesky announcement for an already-published issue (Substack
  // flow). Lands in Evening Review; idempotent if one is already pending.
  // Also flips issue status to PUBLISHED (channel=substack).
  const announceOnBluesky = async () => {
    if (!editing || editing.isNew) return toast.error("Save the draft first.");
    setAnnouncing(true);
    try {
      const { data } = await api.post(`/newsletter/issues/${editing.slug}/announce-bluesky`);
      setEditing(prev => prev ? { ...prev, status: "sent",
                                  published_on: data.published_on || prev.published_on } : prev);
      if (data.already_queued) {
        toast.success("Already queued for Evening Review. Issue marked as published.");
      } else {
        toast.success("Bluesky announcement drafted — see Evening Review. Issue marked as published.");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't draft announcement.");
    } finally {
      setAnnouncing(false);
    }
  };

  // Standalone "mark as published on Substack" without drafting an announcement
  // — for the case where she already announced manually or doesn't want to.
  const markPublished = async (channel = "substack") => {
    if (!editing || editing.isNew) return toast.error("Save the draft first.");
    setBusy(true);
    try {
      const { data } = await api.post(
        `/newsletter/issues/${editing.slug}/mark-published?channel=${channel}`
      );
      setEditing(prev => prev ? { ...prev, status: "sent",
                                  published_on: data.published_on || prev.published_on } : prev);
      toast.success(data.already
        ? `Already marked published on ${channel}.`
        : `Marked as published on ${channel}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update status.");
    } finally { setBusy(false); }
  };

  // ============ PREVIEW MODAL ============
  if (previewHtml) {
    return (
      <div className="max-w-4xl" data-testid="newsletter-preview">
        <div className="flex items-center justify-between mb-6">
          <div className="overline">Preview</div>
          <Button onClick={()=>setPreviewHtml(null)} variant="outline" className="btn-ghost rounded-sm">
            <X size={14} className="mr-2"/>Close preview
          </Button>
        </div>
        <div className="rounded-sm overflow-hidden" style={{background:"#f5ecd9"}}>
          <iframe srcDoc={previewHtml} title="newsletter-preview"
                  style={{width:"100%", height:"800px", border:"none"}}/>
        </div>
      </div>
    );
  }

  // ============ EDITOR ============
  if (editing) {
    return (
      <div className="max-w-4xl" data-testid="newsletter-editor">
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="overline">Newsletter issue · {editing.isNew ? "new draft" : (editing.status || "draft")}</div>
            <h1 className="font-display text-4xl mt-2" style={{color:"var(--elle-ink)"}}>
              {editing.subject || "Untitled issue"}
            </h1>
            <div className="mt-2 text-xs font-mono-elle flex items-center gap-3"
                 data-testid="newsletter-autosave-status"
                 style={{ color: "var(--elle-ink-faint)" }}>
              <Archive size={12}/>
              <span>
                {draftMeta?.saved_at
                  ? `Autosaved locally · ${formatDraftAge(draftMeta.saved_at)}`
                  : "Autosave on — nothing lost if your session times out."}
              </span>
              {draftMeta?.saved_at && (
                <button type="button" onClick={discardDraft}
                        data-testid="newsletter-autosave-discard"
                        className="underline hover:text-[var(--elle-amber)]">
                  Discard local draft
                </button>
              )}
            </div>
          </div>
          <Button onClick={()=>setEditing(null)} variant="outline" className="btn-ghost rounded-sm">
            <X size={14} className="mr-2"/>Close
          </Button>
        </div>

        <div className="space-y-5">
          <div>
            <label className="overline block mb-2">Subject line</label>
            <Input data-testid="issue-subject" value={editing.subject}
                   onChange={(e)=>setEditing({...editing, subject:e.target.value})}
                   placeholder="From the Workshop — week of X"/>
          </div>
          <div>
            <label className="overline block mb-2">Intro (markdown, optional)</label>
            <Textarea data-testid="issue-intro" rows={4} value={editing.intro_markdown}
                      onChange={(e)=>setEditing({...editing, intro_markdown:e.target.value})}
                      placeholder="A couple of lines to open. **Markdown** works."/>
          </div>

          {/* Sections BEFORE the daily briefs */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="overline">Sections before briefs (optional)</label>
              <Button onClick={() => addSection("before")} size="sm" variant="outline"
                      className="btn-ghost rounded-sm"
                      data-testid="add-section-before">
                <Plus size={12} className="mr-1"/>Add section (before)
              </Button>
            </div>
            {(editing.custom_sections || [])
              .map((s, i) => ({ s, i }))
              .filter(({ s }) => (s.position || "after") === "before")
              .map(({ s, i }) => (
              <div key={s._id || `legacy-${i}`} className="elle-surface-2 p-4 rounded-sm mb-3 space-y-2"
                   data-testid={`section-before-${i}`}>
                <div className="flex items-center gap-2">
                  <Input placeholder="Section title"
                         value={s.title} onChange={(e)=>updateSection(i,"title",e.target.value)}/>
                  <Button onClick={()=>removeSection(i)} size="sm" variant="outline" className="btn-ghost rounded-sm">
                    <Trash2 size={12}/>
                  </Button>
                </div>
                <Textarea placeholder="Body (markdown)"
                          rows={4}
                          value={s.body_markdown}
                          onChange={(e)=>updateSection(i,"body_markdown",e.target.value)}/>
              </div>
            ))}
          </div>

          <div>
            <label className="overline block mb-2">Pick briefs from recent Daily Briefs</label>
            {candidates.length === 0 ? (
              <p className="text-xs muted font-mono-elle">
                No briefs in the last 7 days. Visit Daily Brief and generate one first.
              </p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto elle-surface-2 p-3 rounded-sm">
                {candidates.map(b => {
                  const picked = editing.brief_ids.includes(b.id);
                  return (
                    <label key={b.id}
                           data-testid={`brief-pick-${b.id}`}
                           className="flex items-start gap-3 p-2 cursor-pointer rounded-sm"
                           style={{background: picked ? "rgba(197,160,89,0.08)" : "transparent"}}>
                      <input type="checkbox" checked={picked} onChange={()=>toggleBrief(b.id)}
                             className="mt-1"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-mono-elle" style={{color:"var(--elle-ink-faint)"}}>
                          {b.brief_date} · {b.region} · {b.category}
                        </div>
                        <div className="font-display text-lg" style={{color:"var(--elle-ink)"}}>
                          {b.headline}
                        </div>
                        <div className="text-xs mt-1" style={{color:"var(--elle-ink-dim)"}}>
                          {b.whats_happening?.slice(0, 140)}{b.whats_happening?.length > 140 ? "…" : ""}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            <div className="text-xs muted mt-2 font-mono-elle">
              {editing.brief_ids.length} brief(s) selected
            </div>
          </div>

          {/* Sections AFTER the daily briefs (original "Custom sections") */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="overline">Sections after briefs (optional)</label>
              <Button onClick={() => addSection("after")} size="sm" variant="outline"
                      className="btn-ghost rounded-sm"
                      data-testid="add-section-after">
                <Plus size={12} className="mr-1"/>Add section (after)
              </Button>
            </div>
            {(editing.custom_sections || [])
              .map((s, i) => ({ s, i }))
              .filter(({ s }) => (s.position || "after") === "after")
              .map(({ s, i }) => (
              <div key={s._id || `legacy-${i}`} className="elle-surface-2 p-4 rounded-sm mb-3 space-y-2"
                   data-testid={`section-after-${i}`}>
                <div className="flex items-center gap-2">
                  <Input placeholder="Section title"
                         value={s.title} onChange={(e)=>updateSection(i,"title",e.target.value)}/>
                  <Button onClick={()=>removeSection(i)} size="sm" variant="outline" className="btn-ghost rounded-sm">
                    <Trash2 size={12}/>
                  </Button>
                </div>
                <Textarea placeholder="Body (markdown)"
                          rows={4}
                          value={s.body_markdown}
                          onChange={(e)=>updateSection(i,"body_markdown",e.target.value)}/>
              </div>
            ))}
          </div>

          <div data-testid="newsletter-header-image-block">
            <div className="flex items-center justify-between mb-2">
              <label className="overline flex items-center gap-2">
                <ImageIcon size={12}/> Header image <span style={{ color: "var(--elle-ink-faint)" }}>(optional)</span>
              </label>
              <div className="flex gap-2 flex-wrap">
                <input type="file"
                       ref={fileInputRef}
                       accept="image/png,image/jpeg,image/webp"
                       style={{ display: "none" }}
                       onChange={(e) => uploadHeaderImage(e.target.files)}
                       data-testid="upload-header-input"/>
                <Button onClick={openCropperForHeader}
                        size="sm"
                        disabled={imgBusy}
                        data-testid="crop-header-btn"
                        className="btn-amber rounded-sm">
                  <Crop size={12} className="mr-1"/>
                  Crop & upload
                </Button>
                <Button onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        size="sm" variant="outline"
                        disabled={imgBusy}
                        data-testid="upload-header-btn"
                        className="btn-ghost rounded-sm">
                  <Upload size={12} className="mr-1"/>
                  {imgBusy ? "Uploading…" : "Upload as-is"}
                </Button>
              </div>
            </div>
            <p className="text-xs font-mono-elle mb-3" style={{ color: "var(--elle-ink-faint)" }}>
              Substack renders header images at <strong style={{ color: "var(--elle-ink-dim)" }}>1456×816 px (~16:9)</strong>.
              Use <em>Crop & upload</em> for pixel-perfect sizing with preview, or <em>Upload as-is</em> if your art
              is already at the correct dimensions. PNG / JPG / WebP, max 10 MB source.
            </p>
            {(editing.header_candidates || []).length === 0 ? (
              <div className="elle-surface-2 p-4 rounded-sm text-xs"
                   style={{ color: "var(--elle-ink-dim)" }}>
                No header image yet. Click "Upload image" above. The selected image appears
                at the top of the Resend email and inside the Substack markdown copy.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {(editing.header_candidates || []).map((c) => {
                  const selected = editing.header_image_id === c.id;
                  return (
                    <div key={c.id} className="relative group">
                      <button type="button"
                              onClick={() => chooseHeader(c.id)}
                              data-testid={`header-candidate-${c.id}`}
                              className="w-full rounded-sm overflow-hidden transition-all block"
                              style={{
                                border: selected ? "2px solid var(--elle-amber)" : "1px solid var(--elle-border)",
                                outline: selected ? "2px solid rgba(197,160,89,0.3)" : "none",
                                outlineOffset: "2px",
                              }}>
                        <img src={c.url} alt={c.filename || "Header candidate"}
                             className="w-full h-auto block"
                             style={{ aspectRatio: "4/1", objectFit: "cover" }}/>
                      </button>
                      {selected && (
                        <div className="absolute top-1 left-1 px-2 py-0.5 text-xs font-mono-elle rounded-sm"
                             style={{ background: "var(--elle-amber)", color: "var(--elle-bg)" }}>
                          <Check size={10} className="inline -mt-0.5 mr-1"/>using
                        </div>
                      )}
                      <button type="button"
                              onClick={(e) => { e.stopPropagation(); removeCandidate(c.id); }}
                              data-testid={`remove-candidate-${c.id}`}
                              title="Remove from list"
                              className="absolute top-1 right-1 p-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                              style={{ background: "rgba(0,0,0,0.6)", color: "#fff" }}>
                        <X size={10}/>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {editing.header_image_id && (
              <button onClick={clearHeader} type="button"
                      data-testid="clear-header-btn"
                      className="mt-2 text-xs font-mono-elle hover:text-[var(--elle-amber)] transition-colors"
                      style={{ color: "var(--elle-ink-dim)" }}>
                × Clear selection (no header)
              </button>
            )}
          </div>

          <div>
            <label className="overline block mb-2">Outro (markdown, optional)</label>
            <Textarea data-testid="issue-outro" rows={3} value={editing.outro_markdown}
                      onChange={(e)=>setEditing({...editing, outro_markdown:e.target.value})}
                      placeholder="Sign-off, or a quiet line to end on."/>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Button onClick={saveIssue} disabled={busy} className="btn-ghost rounded-sm" variant="outline"
                  data-testid="issue-save">
            <Save size={14} className="mr-2"/>Save draft
          </Button>
          <Button onClick={preview} disabled={busy || editing.isNew} className="btn-ghost rounded-sm" variant="outline"
                  data-testid="issue-preview">
            <Eye size={14} className="mr-2"/>Preview
          </Button>
          {editing.status !== "sent" && !editing.isNew && (
            <Button onClick={send} disabled={busy || !subsInfo?.stats.confirmed}
                    className="btn-amber rounded-sm"
                    data-testid="issue-send">
              <Send size={14} className="mr-2"/>
              Send to {subsInfo?.stats.confirmed || 0} subscriber{(subsInfo?.stats.confirmed || 0) === 1 ? "" : "s"}
            </Button>
          )}
          {editing.status === "sent" && (
            <div className="px-4 py-2 text-xs font-mono-elle flex items-center"
                 style={{color:"var(--elle-moss)", border:"1px solid var(--elle-moss)"}}>
              SENT · {editing.recipients_count || 0} recipient(s)
            </div>
          )}
        </div>

        {/* ========= Substack cross-post helper ========= */}
        {!editing.isNew && (
          <div className="mt-8 elle-surface-2 rounded-sm p-5"
               data-testid="substack-crosspost-block"
               style={{ borderLeft: "3px solid var(--elle-amber)" }}>
            <div className="flex items-start gap-3 flex-wrap">
              <Send size={18} className="mt-1" style={{ color: "var(--elle-amber)" }}/>
              <div className="flex-1 min-w-[240px]">
                <div className="font-display text-xl" style={{ color: "var(--elle-ink)" }}>
                  Also publish to Substack
                </div>
                <p className="text-sm mt-1" style={{ color: "var(--elle-ink-dim)" }}>
                  One click: Substack-ready version copies to your clipboard and
                  the Substack editor opens in a new tab. Paste (⌘/Ctrl&nbsp;+&nbsp;V),
                  check, hit publish. ~20&nbsp;seconds.
                </p>
              </div>
              <Button onClick={copyForSubstack} disabled={busy}
                      className="btn-amber rounded-sm"
                      data-testid="substack-copy-open">
                <ClipboardCopy size={14} className="mr-2"/>
                Copy &amp; open Substack
              </Button>
            </div>
            <div className="text-xs font-mono-elle mt-3 flex items-center gap-3 flex-wrap"
                 style={{ color: "var(--elle-ink-faint)" }}>
              <span>UTM-tagged links auto-included so you can see referral sources in Substack stats.</span>
              <a href="/dashboard/substack" className="underline hover:text-[var(--elle-amber)]"
                 data-testid="substack-dashboard-link">
                <ExternalLink size={11} className="inline mr-1"/>
                Log today's subscriber count
              </a>
            </div>
            {/* ---- Announce on Bluesky after Substack publish ---- */}
            <div className="mt-5 pt-5 border-t flex items-start gap-3 flex-wrap"
                 style={{ borderColor: "rgba(197,160,89,0.15)" }}>
              <Radio size={16} className="mt-1" style={{ color: "var(--elle-moss)" }}/>
              <div className="flex-1 min-w-[240px]">
                <div className="text-sm" style={{ color: "var(--elle-ink)" }}>
                  Already published on Substack?
                </div>
                <p className="text-xs mt-1" style={{ color: "var(--elle-ink-dim)" }}>
                  Either button marks this issue as <strong>PUBLISHED</strong> in the dashboard.
                  The first also drafts a Bluesky announcement post that lands in Evening Review
                  for approval.
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {editing.status !== "sent" && (
                  <Button onClick={() => markPublished("substack")} disabled={busy || announcing}
                          data-testid="mark-published-btn"
                          variant="outline" className="btn-ghost rounded-sm">
                    <Check size={14} className="mr-2"/>
                    Just mark as published
                  </Button>
                )}
                <Button onClick={announceOnBluesky} disabled={busy || announcing}
                        data-testid="announce-bluesky-btn"
                        variant="outline" className="btn-ghost rounded-sm">
                  <Radio size={14} className="mr-2"/>
                  {announcing ? "Drafting…" : "Announce on Bluesky"}
                </Button>
              </div>
            </div>
            {editing.status === "sent" && (editing.published_on || []).length > 0 && (
              <div className="mt-4 text-xs font-mono-elle flex items-center gap-2"
                   data-testid="published-status-chip"
                   style={{ color: "var(--elle-moss)" }}>
                <Check size={12}/>
                PUBLISHED on {(editing.published_on || []).join(", ")}
              </div>
            )}
          </div>
        )}
        <ImageCropModal
          open={cropOpen}
          platform="substack"
          onClose={() => setCropOpen(false)}
          onSave={onCroppedHeader}
        />
      </div>
    );
  }

  // ============ LIST ============
  const stats = subsInfo?.stats || {};
  return (
    <div className="max-w-5xl" data-testid="newsletter-page">
      <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Newsletter</div>
          <h1 className="font-display text-5xl mt-2" style={{color:"var(--elle-ink)"}}>
            From the Workshop
          </h1>
        </div>
        <Button onClick={newIssue} className="btn-amber rounded-sm" data-testid="issue-new">
          <Plus size={14} className="mr-2"/>New issue
        </Button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-4 mb-10">
        <div className="elle-surface p-5 rounded-sm">
          <div className="overline flex items-center gap-2"><Users size={12}/>Confirmed</div>
          <div className="font-display text-4xl mt-2" style={{color:"var(--elle-ink)"}}>{stats.confirmed ?? "…"}</div>
        </div>
        <div className="elle-surface p-5 rounded-sm">
          <div className="overline flex items-center gap-2"><Mail size={12}/>Pending confirm</div>
          <div className="font-display text-4xl mt-2" style={{color:"var(--elle-ink-dim)"}}>{stats.pending ?? "…"}</div>
        </div>
        <div className="elle-surface p-5 rounded-sm">
          <div className="overline flex items-center gap-2"><X size={12}/>Unsubscribed</div>
          <div className="font-display text-4xl mt-2" style={{color:"var(--elle-ink-faint)"}}>{stats.unsubscribed ?? "…"}</div>
        </div>
      </div>

      <div className="elle-surface rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="overline text-left" style={{color:"var(--elle-ink-dim)"}}>
              <th className="p-3">Subject</th>
              <th className="p-3">Status</th>
              <th className="p-3">Recipients</th>
              <th className="p-3">Updated</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {issues === null && <tr><td colSpan={5} className="p-8 text-center muted">Loading…</td></tr>}
            {issues && issues.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center">
                <Mail size={28} className="mx-auto mb-2" style={{color:"var(--elle-ink-faint)"}}/>
                <p style={{color:"var(--elle-ink-dim)"}}>No issues yet. Compose the first one.</p>
              </td></tr>
            )}
            {issues?.map(i => (
              <tr key={i.slug} className="border-t" style={{borderColor:"var(--elle-line)"}}
                  data-testid={`issue-row-${i.slug}`}>
                <td className="p-3">
                  <button onClick={()=>openIssue(i)} className="text-left hover:underline"
                          style={{color:"var(--elle-ink)"}}>
                    {i.subject}
                  </button>
                </td>
                <td className="p-3 font-mono-elle text-xs"
                    style={{color: i.status === "sent" ? "var(--elle-moss)" : "var(--elle-ink-faint)"}}>
                  {i.status === "sent"
                    ? `PUBLISHED${(i.published_on && i.published_on.length)
                        ? " · " + i.published_on.join(", ")
                        : ""}`
                    : (i.status || "draft").toUpperCase()}
                </td>
                <td className="p-3 font-mono-elle text-xs" style={{color:"var(--elle-ink-dim)"}}>
                  {i.recipients_count || "—"}
                </td>
                <td className="p-3 font-mono-elle text-xs" style={{color:"var(--elle-ink-dim)"}}>
                  {formatDate(i.updated_at)}
                </td>
                <td className="p-3 text-right">
                  {i.status !== "sent" && (
                    <Button size="sm" variant="outline" className="btn-ghost rounded-sm"
                            onClick={()=>deleteIssue(i.slug)}
                            data-testid={`issue-delete-${i.slug}`}>
                      <Trash2 size={12}/>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
